import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { UserContext } from "../../Context/Context";
import api from "../../../api/axios";

/**
 * Hook that:
 *  - Fetches the initial unread message count for the logged-in user.
 *  - Listens on the private WebSocket channel `user.{id}` for
 *    `NewNotificationEvent` and increments the counter in real time.
 *  - Listens for the DOM event `messages:marked-read` dispatched by Mailbox
 *    after a successful mark-read API call and re-fetches the real count.
 */
export function useUnreadMessages() {
  const { userData } = useContext(UserContext);
  const { user } = userData;
  const userId = Number(user?.id || 0);

  const [unreadCount, setUnreadCount] = useState(0);
  const [unreadItems, setUnreadItems] = useState([]);
  const processedEventIdsRef = useRef(new Set());

  // ------------------------------------------------------------------
  // 1. Fetch initial unread count from the API
  // ------------------------------------------------------------------
  const fetchUnreadCount = useCallback(async () => {
    if (!userId) return;
    try {
      const { data } = await api.post(
        `${process.env.REACT_APP_API_URL}/messages/unread-count`,
      );
      setUnreadCount(data?.unread_count ?? 0);
    } catch {
      // Silently ignore — the badge simply won't show a stale number
    }
  }, [userId]);

  const fetchUnreadItems = useCallback(async () => {
    if (!userId) return;
    try {
      const { data } = await api.post(
        `${process.env.REACT_APP_API_URL}/messages/unread-latest`,
        { limit: 8 },
      );
      setUnreadItems(Array.isArray(data?.items) ? data.items : []);
    } catch {
      // Keep current list on transient failures
    }
  }, [userId]);

  useEffect(() => {
    fetchUnreadCount();
    fetchUnreadItems();
  }, [fetchUnreadCount, fetchUnreadItems]);

  // ------------------------------------------------------------------
  // 2. Receive websocket notifications from useEcho (window event bridge)
  // ------------------------------------------------------------------
  useEffect(() => {
    if (!userId) return;

    const handler = (event) => {
      const payload = event?.detail || {};
      const msg = payload?.msg || payload || {};
      const msgTo = Number(msg?.to || 0);
      const msgId = Number(msg?.id || 0);

      if (msgTo && msgTo !== userId) {
        return;
      }

      if (msgId > 0) {
        if (processedEventIdsRef.current.has(msgId)) {
          return;
        }
        processedEventIdsRef.current.add(msgId);

        const nextItem = {
          id: msg.id,
          subject: msg.subject || "Nuevo mensaje",
          date: msg.date || new Date().toISOString(),
          status: msg.status || "unread",
          case_id: msg.case_id || null,
          fromRelation: msg.fromRelation || null,
        };

        setUnreadItems((prev) => {
          const filtered = prev.filter(
            (item) => Number(item?.id || 0) !== msgId,
          );
          return [nextItem, ...filtered].slice(0, 8);
        });

        setUnreadCount((prev) => prev + 1);
        return;
      }

      fetchUnreadCount();
      fetchUnreadItems();
    };

    window.addEventListener("messages:new-notification", handler);
    return () =>
      window.removeEventListener("messages:new-notification", handler);
  }, [userId, fetchUnreadCount, fetchUnreadItems]);

  // ------------------------------------------------------------------
  // 3. Listen for DOM event dispatched by Mailbox after mark-read
  // ------------------------------------------------------------------
  useEffect(() => {
    const handler = () => {
      fetchUnreadCount();
      fetchUnreadItems();
    };
    window.addEventListener("messages:marked-read", handler);
    return () => window.removeEventListener("messages:marked-read", handler);
  }, [fetchUnreadCount, fetchUnreadItems]);

  useEffect(() => {
    const handler = () => {
      fetchUnreadCount();
      fetchUnreadItems();
    };

    window.addEventListener("messages:refresh-unread", handler);
    return () => window.removeEventListener("messages:refresh-unread", handler);
  }, [fetchUnreadCount, fetchUnreadItems]);

  // Periodic reconciliation for production resilience (socket drops/reconnects).
  useEffect(() => {
    if (!userId) return;

    const intervalId = window.setInterval(() => {
      if (document.visibilityState === "visible") {
        fetchUnreadCount();
        fetchUnreadItems();
      }
    }, 45000);

    return () => window.clearInterval(intervalId);
  }, [userId, fetchUnreadCount, fetchUnreadItems]);

  return {
    unreadCount,
    unreadItems,
    refreshUnread: () => {
      fetchUnreadCount();
      fetchUnreadItems();
    },
  };
}
